<template>
  <div class="container mx-auto px-4 py-8">
    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-white mb-4">Search History</h1>
          <p class="text-gray-400">View and analyze your organization's search data</p>
        </div>
        <div>
          <button
            @click="refreshSearches"
            class="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-dark px-4 py-2 text-sm font-medium text-gray-300 hover:border-primary/30 hover:bg-primary/10 hover:text-primary transition-colors"
            :disabled="loading"
          >
            <svg 
              class="h-4 w-4"
              :class="{ 'animate-spin': loading }"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ loading ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Metric Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <!-- Search Volume -->
      <div class="bg-dark-secondary rounded-lg p-6">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-gray-400">Search Volume</h3>
          <svg class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <div v-if="loadingMetrics || !metricsInitialized" class="mt-2 space-y-2">
          <div class="h-9 bg-gray-700/50 rounded animate-pulse w-24"></div>
          <div class="h-4 w-32 bg-gray-700/50 rounded animate-pulse"></div>
        </div>
        <div v-else>
          <p class="mt-2 text-3xl font-semibold text-white">{{ searchVolume }}</p>
          <p class="mt-1 text-sm text-gray-400">Total searches in period</p>
        </div>
      </div>

      <!-- Average Order Value -->
      <div class="bg-dark-secondary rounded-lg p-6">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-gray-400">Average Order Value</h3>
          <svg class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div v-if="loadingMetrics || !metricsInitialized" class="mt-2 space-y-2">
          <div class="h-9 bg-gray-700/50 rounded animate-pulse w-28"></div>
          <div class="h-4 w-40 bg-gray-700/50 rounded animate-pulse"></div>
        </div>
        <div v-else>
          <p class="mt-2 text-3xl font-semibold text-white">{{ formattedAOV }}</p>
          <p class="mt-1 text-sm text-gray-400">Average order value from conversions</p>
        </div>
      </div>

      <!-- Conversion Rate -->
      <div class="bg-dark-secondary rounded-lg p-6">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-gray-400">Conversion Rate</h3>
          <svg class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <div v-if="loadingMetrics || !metricsInitialized" class="mt-2 space-y-2">
          <div class="h-9 bg-gray-700/50 rounded animate-pulse w-20"></div>
          <div class="h-4 w-36 bg-gray-700/50 rounded animate-pulse"></div>
        </div>
        <div v-else>
          <p class="mt-2 text-3xl font-semibold text-white">{{ formattedConversionRate }}</p>
          <p class="mt-1 text-sm text-gray-400">Searches resulting in purchase</p>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-dark-secondary rounded-lg p-6 mb-8 relative">
      <div class="space-y-4">
        <!-- Top Row: Search, Date Range and Conversion Status -->
        <div class="flex gap-4">
          <!-- Search -->
          <div class="relative flex-1">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              v-model="searchTerm"
              type="search"
              class="block w-full rounded-lg border border-gray-700 bg-dark pl-10 pr-3 py-2 text-sm text-gray-100 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Search queries..."
            />
          </div>

          <!-- Date Range -->
          <div class="relative">
            <!-- Date Display Button -->
            <button 
              @click="showDatePicker = !showDatePicker"
              class="date-picker-trigger flex items-center gap-2 bg-dark border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-100 hover:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{{ formatDateRange }}</span>
            </button>

            <!-- Date Picker Dropdown -->
            <div 
              v-if="showDatePicker"
              class="date-picker absolute left-0 top-full mt-2 z-10 bg-dark-secondary rounded-xl shadow-lg border border-gray-700 p-4 min-w-[320px]"
            >
              <!-- Calendar Header -->
              <div class="flex items-center justify-between mb-4">
                <button 
                  @click="changeMonth(-1)"
                  class="p-1 hover:bg-gray-700/50 rounded-lg text-gray-400 hover:text-white"
                >
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 class="text-sm font-medium text-gray-100">{{ currentMonthYear }}</h3>
                <button 
                  @click="changeMonth(1)"
                  class="p-1 hover:bg-gray-700/50 rounded-lg text-gray-400 hover:text-white"
                >
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <!-- Calendar Grid -->
              <div class="grid grid-cols-7 gap-1 mb-4">
                <!-- Day Headers -->
                <div v-for="day in ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']" 
                     :key="day"
                     class="text-center text-xs font-medium text-gray-400 py-1"
                >
                  {{ day }}
                </div>

                <!-- Calendar Days -->
                <button
                  v-for="(date, index) in calendarDays"
                  :key="index"
                  @click="selectDate(date)"
                  :class="[
                    'text-center py-1 text-sm rounded transition-colors',
                    date.isCurrentMonth ? 'text-gray-100' : 'text-gray-600',
                    isSelectedDate(date) ? 'bg-primary text-white' : 'hover:bg-gray-700/50',
                    isInRange(date) ? 'bg-primary/20' : ''
                  ]"
                  :disabled="!date.isCurrentMonth"
                >
                  {{ date.day }}
                </button>
              </div>

              <!-- Preset Actions -->
              <div class="grid grid-cols-2 gap-2 mb-4">
                <button
                  v-for="preset in datePresets"
                  :key="preset.value"
                  @click="selectDatePreset(preset)"
                  class="text-sm text-gray-400 hover:text-white py-1 px-2 rounded transition-colors hover:bg-gray-700/50"
                >
                  {{ preset.label }}
                </button>
              </div>

              <!-- Action Buttons -->
              <div class="flex justify-between border-t border-gray-700 pt-4">
                <button
                  @click="() => { showDatePicker = false }"
                  class="text-sm text-gray-400 hover:text-white py-1 px-3 rounded transition-colors hover:bg-gray-700/50"
                >
                  Cancel
                </button>
                <div class="flex gap-2">
                  <button
                    @click="setToday"
                    class="text-sm text-gray-400 hover:text-white py-1 px-3 rounded transition-colors hover:bg-gray-700/50"
                  >
                    Today
                  </button>
                  <button
                    @click="applyDateRange"
                    class="text-sm text-white bg-primary py-1 px-3 rounded transition-colors hover:bg-primary/90"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Conversion Status and Spam Toggle -->
          <div class="flex items-center gap-3 pl-4 border-l border-gray-700">
            <div class="flex rounded-lg border border-gray-700 p-1">
              <button
                v-for="status in conversionFilters"
                :key="status.value"
                @click="toggleConversionFilter(status.value)"
                :class="[
                  'flex items-center gap-1.5 rounded px-2.5 py-1 text-sm font-medium transition-colors',
                  filters.conversion === status.value
                    ? 'bg-primary text-white'
                    : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
                ]"
              >
                <span 
                  v-if="status.value !== ''"
                  class="relative flex h-2 w-2"
                >
                  <span :class="[
                    'relative inline-flex h-2 w-2 rounded-full',
                    status.value === 'converted' ? 'bg-emerald-500' : 'bg-gray-500'
                  ]"></span>
                </span>
                {{ status.label }}
              </button>
            </div>

            <!-- Spam Toggle -->
            <div class="flex items-center gap-2 pl-3">
              <button
                @click="toggleIncludeSpam"
                class="relative inline-flex h-6 w-11 items-center rounded-full"
                :class="includeSpam ? 'bg-orange-500/30' : 'bg-gray-700'"
              >
                <span class="sr-only">Include spam searches</span>
                <span
                  class="inline-block h-4 w-4 transform rounded-full bg-white transition"
                  :class="includeSpam ? 'translate-x-6' : 'translate-x-1'"
                ></span>
              </button>
              <span class="text-sm text-gray-400">Include Spam</span>
            </div>
          </div>
        </div>

        <!-- Categories Row -->
        <div class="flex gap-4">
          <!-- Categories Filter -->
          <div class="relative categories-filter flex-1">
            <div class="flex flex-wrap items-center gap-2 rounded-lg border border-gray-700 bg-dark p-2 min-h-[40px] focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
              <!-- Selected Categories -->
              <div
                v-for="category in filters.categories"
                :key="category"
                class="inline-flex items-center gap-1 rounded-full bg-primary/20 px-2 py-1 text-xs font-medium text-primary"
              >
                {{ formatCategory(category) }}
                <button
                  @click="toggleCategory(category)"
                  class="text-primary/70 hover:text-primary"
                  aria-label="Remove category"
                >×</button>
              </div>
              
              <!-- Search Input -->
              <input
                v-model="categorySearchQuery"
                type="text"
                class="flex-1 min-w-[120px] bg-transparent text-sm text-gray-100 placeholder-gray-400 focus:outline-none"
                placeholder="Filter categories..."
                @focus="showCategorySuggestions = true"
                @keydown.backspace="handleBackspace"
              />
            </div>
          </div>

          <!-- Category Suggestions Dropdown -->
          <div
            v-if="showCategorySuggestions && filteredAvailableCategories.length > 0"
            class="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-700 bg-dark-secondary shadow-lg"
          >
            <button
              v-for="category in filteredAvailableCategories"
              :key="category"
              @click="selectCategory(category)"
              class="w-full px-3 py-2 text-left text-sm text-gray-200 transition-colors hover:bg-gray-700/50"
            >
              {{ formatCategory(category) }}
            </button>
          </div>
        </div>

        <!-- Active Filters Summary -->
        <div v-if="hasActiveFilters" class="flex items-center justify-between border-t border-gray-700 pt-3">
          <span class="text-sm text-gray-400">
            Showing {{ paginatedSearches.length }} results
          </span>

          <button
            @click="clearFilters"
            class="text-sm text-gray-400 hover:text-white"
          >
            Clear filters
          </button>
        </div>
      </div>
    </div>

    <!-- Top Pagination -->
    <div v-if="!loading && (hasPreviousPage || hasNextPage || currentPage > 1)" class="mb-4 flex items-center justify-between">
      <div class="flex items-center gap-2 text-sm text-gray-400">
        <span>Page</span>
        <span class="text-white font-medium">{{ currentPage }}</span>
        <span class="text-gray-500">({{ paginatedSearches.length }} results)</span>
      </div>
      
      <div class="flex items-center gap-2">
        <button
          @click="goToFirstPage"
          :disabled="!hasPreviousPage"
          class="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-700 bg-dark text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
          title="First page"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
        <button
          @click="previousPage"
          :disabled="!hasPreviousPage"
          class="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-700 bg-dark text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
          title="Previous page"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button
          @click="nextPage"
          :disabled="!hasNextPage"
          class="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-700 bg-dark text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
          title="Next page"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Results -->
    <div v-if="loading" class="overflow-x-auto">
      <div class="bg-dark-secondary rounded-xl shadow-lg">
        <table class="min-w-full">
          <thead class="border-b border-gray-700">
            <tr>
              <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
                Search Time
              </th>
              <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
                Search Query
              </th>
              <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
                Product Link
              </th>
              <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
                Categories
              </th>
              <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
                Converted
              </th>
              <th scope="col" class="px-4 py-4 text-center text-xs font-medium text-gray-300 uppercase">
                Details
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-700/50">
            <!-- Skeleton loader rows -->
            <tr v-for="i in 5" :key="`skeleton-${i}`" class="hover:bg-gray-700/20">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="h-4 bg-gray-700/50 rounded animate-pulse w-24 mx-auto"></div>
              </td>
              <td class="px-6 py-4">
                <div class="flex items-start gap-2">
                  <div class="h-4 bg-gray-700/50 rounded animate-pulse w-48"></div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="h-4 bg-gray-700/50 rounded animate-pulse w-40"></div>
              </td>
              <td class="px-6 py-4">
                <div class="flex flex-wrap gap-1">
                  <div class="h-6 bg-gray-700/50 rounded-md animate-pulse w-16"></div>
                  <div class="h-6 bg-gray-700/50 rounded-md animate-pulse w-20"></div>
                </div>
              </td>
              <td class="px-6 py-4 text-center">
                <div class="h-8 w-8 bg-gray-700/50 rounded-full animate-pulse mx-auto"></div>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-right">
                <div class="flex items-center gap-2 justify-end">
                  <div class="h-8 w-8 bg-gray-700/50 rounded-lg animate-pulse"></div>
                  <div class="h-8 w-8 bg-gray-700/50 rounded-lg animate-pulse"></div>
                  <div class="h-8 w-8 bg-gray-700/50 rounded-lg animate-pulse"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else class="overflow-x-auto">
      <div class="bg-dark-secondary rounded-xl shadow-lg">
        <table class="min-w-full">
          <thead class="border-b border-gray-700">
            <tr>
            <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
              Search Time
            </th>
            <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
              Search Query
            </th>
            <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
              Product Link
            </th>
            <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
              Categories
            </th>
            <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase">
              Converted
            </th>
            <th scope="col" class="px-4 py-4 text-center text-xs font-medium text-gray-300 uppercase">
              Details
            </th>
          </tr>
          </thead>
          <tbody class="divide-y divide-gray-700/50">
            <tr v-if="!paginatedSearches.length" class="hover:bg-gray-700/20 transition-colors">
              <td colspan="6" class="px-6 py-4 text-sm text-gray-500 text-center">
                No searches found
              </td>
            </tr>
            <tr v-else v-for="search in paginatedSearches" 
                :key="search.id"
                class="hover:bg-gray-700/20 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                {{ formatDate(search.created_at) }}
              </td>
              <td class="px-6 py-4 text-sm text-gray-300">
                <div class="flex items-start gap-2">
                  <div class="break-words min-w-[200px] max-w-[300px]">
                    {{ search.query }}
                  </div>
                  <div v-if="search.enriched_data?.is_potential_spam" 
                       class="relative group flex-shrink-0 mt-0.5">
                    <svg class="h-4 w-4 text-amber-500" 
                         fill="none" 
                         viewBox="0 0 24 24" 
                         stroke="currentColor">
                      <path stroke-linecap="round" 
                            stroke-linejoin="round" 
                            stroke-width="2" 
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block">
                      <div class="bg-dark-secondary text-xs text-amber-500 rounded-md px-2 py-1 whitespace-nowrap border border-gray-700">
                        Potential Spam
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 text-sm text-gray-300">
                <div class="min-w-[200px] max-w-[300px]">
                  <a v-if="search.response?.product_url"
                     :href="getFullProductUrl(search.response.product_url)"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="text-blue-400 hover:text-blue-300 flex items-start">
                    <span class="break-words">{{ search.response.product_url }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <span v-else class="text-gray-500">-</span>
                </div>
              </td>
              <td class="px-6 py-4 text-sm">
                <div v-if="search.enriched_data?.enriched_categories?.length" class="flex flex-wrap gap-1">
                  <span v-for="category in search.enriched_data.enriched_categories" 
                        :key="category" 
                        class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-700/50 text-gray-300">
                    {{ formatCategory(category) }}
                  </span>
                </div>
                <span v-else class="text-gray-500">-</span>
              </td>
              <td class="px-6 py-4 text-sm text-center">
                <div v-if="search.has_converted" 
                     class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/10 text-emerald-500">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span v-else class="text-gray-500">-</span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-right text-sm">
                  <div class="flex items-center gap-2 justify-end">
                    <button
                      @click="toggleSpamStatus(search)"
                      class="inline-flex items-center justify-center w-8 h-8 rounded-lg border"
                      :class="[
                        search.enriched_data?.is_potential_spam 
                          ? 'border-amber-500/30 text-amber-500 hover:border-amber-500/50 hover:bg-amber-500/10'
                          : 'border-gray-700 text-gray-400 hover:border-amber-500/30 hover:bg-amber-500/10 hover:text-amber-500'
                      ]"
                      :title="search.enriched_data?.is_potential_spam ? 'Mark as Not Spam' : 'Mark as Spam'"
                    >
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </button>
                    <button
                      @click="handleDeleteSearch(search)"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-500/30 text-red-500 hover:border-red-500/50 hover:bg-red-500/10 transition-colors"
                    title="Delete Search"
                  >
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <button 
                    @click="openDetailsModal(search)"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-700 text-gray-300 hover:border-primary/30 hover:bg-primary/10 hover:text-primary transition-colors"
                    title="View Details"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
        </tbody>
      </table>
      </div>

      <!-- Pagination -->
      <div v-if="hasPreviousPage || hasNextPage || currentPage > 1" class="mt-6 flex items-center justify-between border-t border-gray-700 px-6 py-4">
        <div class="flex items-center gap-2 text-sm text-gray-400">
          <span>Showing</span>
          <span class="text-white font-medium">{{ paginatedSearches.length }}</span>
          <span>results on page</span>
          <span class="text-white font-medium">{{ currentPage }}</span>
        </div>
        
        <div class="flex items-center gap-2">
          <button
            @click="goToFirstPage"
            :disabled="!hasPreviousPage"
            class="inline-flex items-center gap-1 rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm font-medium text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            First
          </button>
          <button
            @click="previousPage"
            :disabled="!hasPreviousPage"
            class="inline-flex items-center gap-1 rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm font-medium text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          
          <button
            @click="nextPage"
            :disabled="!hasNextPage"
            class="inline-flex items-center gap-1 rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm font-medium text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
          >
            Next
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
    <!-- Details Modal -->
    <BaseModal
      v-model="showDetailsModal"
      max-width="80rem"
    >
      <template #title>
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-medium text-white">Search Details</h3>
          <button
            @click="closeDetailsModal"
            class="text-gray-400 hover:text-gray-300 focus:outline-none"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </template>

      <template #footer>
        <div class="flex items-center justify-between w-full">
          <span class="text-xs text-gray-500">ID: {{ selectedSearch?.id }}</span>
          <button
            @click="() => selectedSearch && handleDeleteSearch(selectedSearch)"
            class="inline-flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-500/20 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-dark disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isDeleting"
          >
          <svg 
            v-if="isDeleting" 
            class="animate-spin h-4 w-4" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <svg 
            v-else
            class="h-4 w-4" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          {{ isDeleting ? 'Deleting...' : 'Delete Search' }}
        </button>
        </div>
      </template>

      <div v-if="selectedSearch" class="grid grid-cols-12 gap-6">
        <!-- Left Column: Search Info -->
        <div class="col-span-4 space-y-6 border-r border-gray-700 pr-6">
          <div class="space-y-4">
            <!-- Query and Time -->
            <div class="pb-4 border-b border-gray-700">
              <div class="flex items-center justify-between mb-2">
                <h4 class="text-sm font-medium text-gray-400">Query</h4>
                <span class="text-sm text-gray-400">{{ formatDate(selectedSearch.created_at) }}</span>
              </div>
              <div class="flex items-start gap-2">
                <p class="text-lg text-white font-medium break-all">{{ selectedSearch.query }}</p>
                <div v-if="selectedSearch.enriched_data?.is_potential_spam" 
                     class="relative group flex-shrink-0 mt-1">
                  <svg class="h-5 w-5 text-amber-500" 
                       fill="none" 
                       viewBox="0 0 24 24" 
                       stroke="currentColor">
                    <path stroke-linecap="round" 
                          stroke-linejoin="round" 
                          stroke-width="2" 
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block">
                    <div class="bg-dark-secondary text-xs text-amber-500 rounded-md px-2 py-1 whitespace-nowrap border border-gray-700">
                      Potential Spam
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Conversion Status -->
            <div class="pb-4 border-b border-gray-700">
              <div class="flex justify-between items-start">
                <div>
                  <h4 class="text-sm font-medium text-gray-400 mb-2">Conversion Status</h4>
                  <span v-if="selectedSearch.has_converted" 
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">
                    <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Converted
                  </span>
                  <span v-else class="text-gray-500">Not Converted</span>
                </div>

                <div v-if="selectedSearch.conversion_data?.order_id">
                  <h4 class="text-sm font-medium text-gray-400 mb-2">Order Amount</h4>
                  <p class="text-white">${{ selectedSearch.conversion_data.amount }}</p>
                </div>
              </div>

              <div v-if="selectedSearch.conversion_data?.order_id" class="mt-3">
                <a :href="`https://${orgStore.orgData?.keys?.shopify?.storeName}.myshopify.com/admin/orders/${selectedSearch.conversion_data.order_id}`"
                   target="_blank"
                   class="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  View Order in Shopify
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            <!-- Categories -->
            <div class="pb-4 border-b border-gray-700">
              <h4 class="text-sm font-medium text-gray-400">Categories</h4>
              <div class="mt-2">
                <div v-if="selectedSearch.enriched_data?.enriched_categories?.length" class="flex flex-wrap gap-2">
                  <button
                    v-for="category in selectedSearch.enriched_data.enriched_categories" 
                    :key="category"
                    @click="filterByCategory(category)"
                    class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 hover:text-white transition-colors"
                  >
                    {{ formatCategory(category) }}
                  </button>
                </div>
                <p v-else class="text-gray-500">-</p>
              </div>
            </div>

            <!-- Product URL -->
            <div class="pb-4 border-b border-gray-700">
              <h4 class="text-sm font-medium text-gray-400">Product URL</h4>
              <a v-if="selectedSearch.response?.product_url"
                 :href="getFullProductUrl(selectedSearch.response.product_url)"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="mt-2 text-blue-400 hover:text-blue-300 flex items-start">
                <span class="break-words">{{ selectedSearch.response.product_url }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <p v-else class="mt-2 text-gray-500">-</p>
            </div>

            <!-- Metadata -->
            <div>
              <h4 class="text-sm font-medium text-gray-400 mb-2">Metadata</h4>
              <div v-if="selectedSearch.meta_data && Object.keys(selectedSearch.meta_data).length > 0" class="flex flex-wrap gap-2">
                <span 
                  v-for="(value, key) in selectedSearch.meta_data" 
                  :key="key"
                  class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-300"
                >
                  {{ key }}: {{ value }}
                </span>
              </div>
              <p v-else class="text-gray-500">-</p>
            </div>
          </div>

          <!-- Search Analysis -->
          <div v-if="selectedSearch.enriched_data" class="space-y-4">
            <h4 class="text-lg font-medium text-white border-b border-gray-700 pb-2">Search Analysis</h4>
            
            <!-- Key Search Terms -->
            <div v-if="selectedSearch.enriched_data.key_search_terms?.length">
              <h5 class="text-sm font-medium text-gray-400">Key Search Terms</h5>
              <div class="mt-2 flex flex-wrap gap-2">
                <span v-for="term in selectedSearch.enriched_data.key_search_terms" 
                      :key="term"
                      class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-700/50 text-gray-300">
                  {{ term }}
                </span>
              </div>
            </div>

            <!-- Query Intent -->
            <div v-if="selectedSearch.enriched_data.query_intent" class="pb-2">
              <h5 class="text-sm font-medium text-gray-400">Query Intent</h5>
              <div class="mt-2 flex items-center gap-2">
                <span class="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-blue-500/10 text-blue-400">
                  {{ selectedSearch.enriched_data.query_intent }}
                </span>
                <span v-if="selectedSearch.enriched_data.intent_score !== undefined" 
                      class="text-sm text-gray-400">
                  ({{ selectedSearch.enriched_data.intent_score }})
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Response Data -->
        <div class="col-span-8 space-y-6">

          <!-- Personalized Content -->
          <div v-if="selectedSearch.response" class="space-y-4">
            <h4 class="text-lg font-medium text-white border-b border-gray-700 pb-2">Personalized Content</h4>
            
            <!-- Dynamic Data Display -->
            <div v-for="(value, key) in selectedSearch.response" :key="key" class="pb-4">
              <h5 class="text-sm font-medium text-gray-400">{{ formatKey(key) }}</h5>
              
              <!-- Array Type -->
              <ul v-if="Array.isArray(value)" class="mt-3 space-y-3">
                <li v-for="(item, index) in value" 
                    :key="index" 
                    class="flex items-start text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {{ item }}
                </li>
              </ul>

              <!-- Boolean Type -->
              <div v-else-if="typeof value === 'boolean'" class="mt-2 flex items-center">
                <svg v-if="value" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span class="text-white">{{ value ? 'Yes' : 'No' }}</span>
              </div>

              <!-- Number Type -->
              <p v-else-if="typeof value === 'number'" class="mt-2 text-lg text-white">
                {{ value }}
              </p>

              <!-- String Type (default) -->
              <p v-else class="mt-2 text-lg text-white">
                {{ value }}
              </p>
            </div>
          </div>

          <!-- Integration Data -->
          <div v-if="selectedSearch.integration_data" class="space-y-4">
            <h4 class="text-lg font-medium text-white border-b border-gray-700 pb-2">Integration Data</h4>
            
            <!-- Dynamic Data Display -->
            <div v-for="(value, key) in selectedSearch.integration_data" :key="key" class="pb-4">
              <h5 class="text-sm font-medium text-gray-400">{{ formatKey(key) }}</h5>
              
              <!-- Array Type -->
              <ul v-if="Array.isArray(value)" class="mt-3 space-y-3">
                <li v-for="(item, index) in value" 
                    :key="index" 
                    class="flex items-start text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {{ item }}
                </li>
              </ul>

              <!-- Boolean Type -->
              <div v-else-if="typeof value === 'boolean'" class="mt-2 flex items-center">
                <svg v-if="value" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span class="text-white">{{ value ? 'Yes' : 'No' }}</span>
              </div>

              <!-- Number Type -->
              <p v-else-if="typeof value === 'number'" class="mt-2 text-lg text-white">
                {{ value }}
              </p>

              <!-- String Type (default) -->
              <p v-else class="mt-2 text-lg text-white">
                {{ value }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { collection, query, where, orderBy, getDocs, limit, startAfter, QueryDocumentSnapshot, Timestamp, doc, deleteDoc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/config/firebase'
import { useOrgStore } from '@/stores/org'
import BaseModal from '@/components/ui/BaseModal.vue'

// State
const searches = ref<any[]>([])
const loading = ref(true)
const searchTerm = ref('')
const includeSpam = ref(false)

// Pagination state
const pageSize = ref(25)
const currentPage = ref(1)
const lastDocSnapshot = ref<QueryDocumentSnapshot | null>(null)
const pageHistory = ref<QueryDocumentSnapshot[]>([]) // Track document snapshots for each page
const totalFilteredCount = ref<number | null>(null) // Total count of filtered results

// Metrics state
const metrics = ref({
  searchVolume: 0,
  averageOrderValue: 0,
  conversionRate: 0
})
const loadingMetrics = ref(true) // Start with loading state
const metricsInitialized = ref(false) // Track if metrics have been fetched at least once

// Date state
const showDatePicker = ref(false)
const currentDate = ref(new Date())
const selectedStartDate = ref<Date | null>(null)
const selectedEndDate = ref<Date | null>(null)
const tempStartDate = ref<Date | null>(null)
const tempEndDate = ref<Date | null>(null)

const datePresets = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'thisMonth', label: 'This month' }
]

// Calendar computed properties
const currentMonthYear = computed(() => {
  return new Intl.DateTimeFormat('en-US', { 
    month: 'long',
    year: 'numeric'
  }).format(currentDate.value)
})

const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  
  // Get days from previous month
  const daysFromPrevMonth = firstDay.getDay()
  const prevMonth = new Date(year, month, 0)
  const prevMonthDays = []
  
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    prevMonthDays.push({
      date: new Date(year, month - 1, prevMonth.getDate() - i),
      day: prevMonth.getDate() - i,
      isCurrentMonth: false
    })
  }
  
  // Get days from current month
  const currentMonthDays = []
  for (let i = 1; i <= lastDay.getDate(); i++) {
    currentMonthDays.push({
      date: new Date(year, month, i),
      day: i,
      isCurrentMonth: true
    })
  }
  
  // Get days from next month
  const daysFromNextMonth = 42 - (prevMonthDays.length + currentMonthDays.length)
  const nextMonthDays = []
  
  for (let i = 1; i <= daysFromNextMonth; i++) {
    nextMonthDays.push({
      date: new Date(year, month + 1, i),
      day: i,
      isCurrentMonth: false
    })
  }
  
  return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays]
})

const formatDateRange = computed(() => {
  if (!selectedStartDate.value || !selectedEndDate.value) {
    return 'Select date range'
  }
  
  // Format date without time for the range selector
  const formatDateNoTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(date)
  }
  return `${formatDateNoTime(selectedStartDate.value)} - ${formatDateNoTime(selectedEndDate.value)}`
})

// Calendar methods
const changeMonth = (delta: number) => {
  const newDate = new Date(currentDate.value)
  newDate.setMonth(newDate.getMonth() + delta)
  currentDate.value = newDate
}

const selectDate = (date: { date: Date, day: number, isCurrentMonth: boolean }) => {
  if (!date.isCurrentMonth) return
  
  if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
    tempStartDate.value = date.date
    tempEndDate.value = null
  } else {
    if (date.date < tempStartDate.value) {
      tempStartDate.value = date.date
    } else {
      tempEndDate.value = date.date
    }
  }
}

const isSelectedDate = (date: { date: Date }) => {
  if (!tempStartDate.value) return false
  
  const isStart = tempStartDate.value.toDateString() === date.date.toDateString()
  const isEnd = tempEndDate.value?.toDateString() === date.date.toDateString()
  
  return isStart || isEnd
}

const isInRange = (date: { date: Date }) => {
  if (!tempStartDate.value || !tempEndDate.value) return false
  
  return date.date > tempStartDate.value && date.date < tempEndDate.value
}

const selectDatePreset = (preset: { value: string, label: string }) => {
  const end = new Date()
  const start = new Date()
  
  if (preset.value === 'thisMonth') {
    start.setDate(1)
  } else {
    start.setDate(start.getDate() - parseInt(preset.value))
  }
  
  tempStartDate.value = start
  tempEndDate.value = end
  
  // Auto-apply preset selection
  applyDateRange()
}

const setToday = () => {
  const today = new Date()
  tempStartDate.value = today
  tempEndDate.value = today
}

const applyDateRange = () => {
  if (tempStartDate.value && tempEndDate.value) {
    selectedStartDate.value = tempStartDate.value
    selectedEndDate.value = tempEndDate.value
    showDatePicker.value = false
    fetchSearches()
  }
}

// Initialize with last 7 days
onMounted(() => {
  selectDatePreset(datePresets[1])
})

// Filter state
const filters = ref({
  categories: [] as string[],
  conversion: '', // '' = all, 'converted' = converted only, 'not_converted' = not converted only
})

// Categories filter state
const categorySearchQuery = ref('')
const showCategorySuggestions = ref(false)

// Close dropdowns when clicking outside
const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  
  // Handle category suggestions
  if (!target.closest('.categories-filter')) {
    showCategorySuggestions.value = false
  }
  
  // Handle date picker
  if (!target.closest('.date-picker') && !target.closest('.date-picker-trigger')) {
    showDatePicker.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  // Clear debounce timer on unmount
  if (searchTermDebounceTimer) {
    clearTimeout(searchTermDebounceTimer)
    searchTermDebounceTimer = null
  }
})

// Filter options
const conversionFilters = [
  { value: '', label: 'All' },
  { value: 'converted', label: 'Converted' },
  { value: 'not_converted', label: 'Not Converted' }
]

// Available categories computed from search data
const availableCategories = computed(() => {
  const categories = new Set<string>()
  searches.value.forEach(search => {
    if (search.enriched_data?.enriched_categories) {
      search.enriched_data.enriched_categories.forEach((cat: string) => categories.add(cat))
    }
  })
  return Array.from(categories).sort()
})

// Filtered available categories based on search
const filteredAvailableCategories = computed(() => {
  if (!categorySearchQuery.value) return availableCategories.value
  const query = categorySearchQuery.value.toLowerCase()
  return availableCategories.value.filter(
    category => category.toLowerCase().includes(query) && !filters.value.categories.includes(category)
  )
})

// Category methods
const selectCategory = (category: string) => {
  if (!filters.value.categories.includes(category)) {
    filters.value.categories.push(category)
  }
  categorySearchQuery.value = ''
  showCategorySuggestions.value = true
  // Keep focus on input after selection
  const input = document.querySelector('.categories-filter input') as HTMLInputElement
  if (input) {
    input.focus()
  }
  // Watch will trigger metrics update
}

const handleBackspace = () => {
  if (categorySearchQuery.value === '' && filters.value.categories.length > 0) {
    const lastCategory = filters.value.categories[filters.value.categories.length - 1]
    toggleCategory(lastCategory)
  }
}

const toggleCategory = (category: string) => {
  const index = filters.value.categories.indexOf(category)
  if (index === -1) {
    filters.value.categories.push(category)
  } else {
    filters.value.categories.splice(index, 1)
  }
  categorySearchQuery.value = ''
  showCategorySuggestions.value = true
  // Watch will trigger metrics update
}

const toggleConversionFilter = (status: string) => {
  filters.value.conversion = filters.value.conversion === status ? '' : status
  // Watch will trigger metrics update
}

const toggleIncludeSpam = () => {
  includeSpam.value = !includeSpam.value
  // Watch will trigger metrics update
}

// Modal state
const showDetailsModal = ref(false)
const selectedSearch = ref<any>(null)
const isDeleting = ref(false)

// Delete search handlers
const handleDeleteSearch = (search: any) => {
  if (confirm('Are you sure you want to delete this search?')) {
    deleteSearch(search)
  }
}

const deleteSearch = async (search: any) => {
  if (!search?.id || isDeleting.value || !orgStore.orgData?.id) return

  try {
    isDeleting.value = true
    const searchRef = doc(db, 'searches', search.id)
    await deleteDoc(searchRef)
    
    // Remove from local state
    searches.value = searches.value.filter(s => s.id !== search.id)
    
    // If the deleted search is currently shown in the modal, close it
    if (selectedSearch.value?.id === search.id) {
      closeDetailsModal()
    }
  } catch (error) {
    console.error('Error deleting search:', error)
  } finally {
    isDeleting.value = false
  }
}

  // Toggle spam status
  const toggleSpamStatus = async (search: any) => {
    if (!search?.id || !orgStore.orgData?.id) return
    
    try {
      const searchRef = doc(db, 'searches', search.id)
      const newSpamStatus = !search.enriched_data?.is_potential_spam
      
      await updateDoc(searchRef, {
        'enriched_data.is_potential_spam': newSpamStatus
      })
      
      // Update local state
      const updatedSearch = searches.value.find(s => s.id === search.id)
      if (updatedSearch) {
        if (!updatedSearch.enriched_data) {
          updatedSearch.enriched_data = {}
        }
        updatedSearch.enriched_data.is_potential_spam = newSpamStatus
      }
    } catch (error) {
      console.error('Error updating spam status:', error)
    }
  }

  // Has active filters
const hasActiveFilters = computed(() => {
  return searchTerm.value || 
         filters.value.categories.length > 0 ||
         filters.value.conversion !== ''
})

// Clear filters
const clearFilters = () => {
  searchTerm.value = ''
  filters.value.categories = []
  filters.value.conversion = ''
  categorySearchQuery.value = ''
  // Watches will trigger metrics update
  currentPage.value = 1
}

// Modal methods
const openDetailsModal = (search: any) => {
  selectedSearch.value = search
  showDetailsModal.value = true
}

const closeDetailsModal = () => {
  showDetailsModal.value = false
  selectedSearch.value = null
}

const filterByCategory = (category: string) => {
  // Clear existing categories and add the new one
  filters.value.categories = [category]
  // Close the modal
  closeDetailsModal()
}

// Store
const orgStore = useOrgStore()

// URL helper
const getFullProductUrl = (productUrl: string) => {
  if (!productUrl) return ''
  if (productUrl.startsWith('http')) return productUrl
  
  const shopifyDomain = orgStore.orgData?.keys?.shopify?.storeName
  if (!shopifyDomain) return productUrl
  
  // Remove leading slash if present
  const cleanProductUrl = productUrl.startsWith('/') ? productUrl.slice(1) : productUrl
  return `https://${shopifyDomain}.myshopify.com/${cleanProductUrl}`
}

// Fetch searches with Firestore pagination
// When text search or category filters are active, fetch all results and paginate client-side
// Otherwise, use Firestore pagination for better performance
const fetchSearches = async (resetPagination = false) => {
  try {
    loading.value = true
    const orgId = orgStore.orgData?.id

    if (!orgId) {
      console.error('No organization ID found')
      return
    }

    if (!selectedStartDate.value || !selectedEndDate.value) return
    
    const startDate = new Date(selectedStartDate.value)
    startDate.setHours(0, 0, 0, 0) // Start of day
    
    const endDate = new Date(selectedEndDate.value)
    endDate.setHours(23, 59, 59, 999) // End of day

    const searchesRef = collection(db, 'searches')
    
    // If text search or category filters are active, fetch all results (no limit) for client-side filtering
    // Otherwise, use Firestore pagination
    const hasTextSearch = searchTerm.value && searchTerm.value.trim().length > 0
    const hasCategoryFilters = filters.value.categories.length > 0
    const needsAllResults = hasTextSearch || hasCategoryFilters
    
    // Build base query constraints
    const baseConstraints: any[] = [
      where('org_id', '==', orgId),
      where('created_at', '>=', startDate),
      where('created_at', '<=', endDate)
    ]
    
    // Add conversion filter to Firestore query if active
    if (filters.value.conversion === 'converted') {
      baseConstraints.push(where('has_converted', '==', true))
    } else if (filters.value.conversion === 'not_converted') {
      baseConstraints.push(where('has_converted', '==', false))
    }
    
    if (needsAllResults) {
      // Fetch all results when text search or category filters are active
      baseConstraints.push(orderBy('created_at', 'desc'))
      
      const q = query(searchesRef, ...baseConstraints)
      const querySnapshot = await getDocs(q)
      
      searches.value = querySnapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data
        }
      })
      
      // Reset pagination state for client-side pagination
      pageHistory.value = []
      lastDocSnapshot.value = null
      hasMorePages.value = false // Will be computed based on filtered results
    } else {
      // Use Firestore pagination when no text search or category filters
      baseConstraints.push(orderBy('created_at', 'desc'))
      baseConstraints.push(limit(pageSize.value))

      // If resetting pagination or going to first page, start from beginning
      if (resetPagination || currentPage.value === 1) {
        pageHistory.value = []
        lastDocSnapshot.value = null
      } else {
        // For forward navigation, use the last document from the previous page
        // For backward navigation, use the snapshot from two pages back
        const snapshotIndex = currentPage.value - 2
        if (snapshotIndex >= 0 && snapshotIndex < pageHistory.value.length) {
          baseConstraints.push(startAfter(pageHistory.value[snapshotIndex]))
        }
      }

      const q = query(searchesRef, ...baseConstraints)
      const querySnapshot = await getDocs(q)

      // Store the last document snapshot for next page
      if (querySnapshot.docs.length > 0) {
        const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1]
        // Update page history - store snapshot for the current page
        const currentPageIndex = currentPage.value - 1
        if (currentPageIndex >= pageHistory.value.length) {
          // Adding a new page, append the snapshot
          pageHistory.value.push(lastDoc)
        } else {
          // Navigating backwards, truncate and add new snapshot
          pageHistory.value = pageHistory.value.slice(0, currentPageIndex)
          pageHistory.value.push(lastDoc)
        }
        lastDocSnapshot.value = lastDoc
      } else {
        lastDocSnapshot.value = null
      }

      searches.value = querySnapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data
        }
      })

      // Check if there are more pages
      hasMorePages.value = querySnapshot.docs.length === pageSize.value
    }

  } catch (error) {
    console.error('Error fetching searches:', error)
  } finally {
    loading.value = false
  }
}

// Fetch metrics from backend
const fetchMetrics = async () => {
  if (!orgStore.orgData?.id || !selectedStartDate.value || !selectedEndDate.value) {
    // Don't clear loading state if dates aren't set yet - they might be set soon
    // Only clear if org is missing (which means we can't fetch)
    if (!orgStore.orgData?.id) {
      loadingMetrics.value = false
    }
    return
  }
  
  try {
    loadingMetrics.value = true
    
    const startDate = new Date(selectedStartDate.value)
    startDate.setHours(0, 0, 0, 0)
    
    const endDate = new Date(selectedEndDate.value)
    endDate.setHours(23, 59, 59, 999)

    const requestData = {
      org_id: orgStore.orgData.id,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      search_term: searchTerm.value || undefined,
      categories: filters.value.categories.length > 0 ? filters.value.categories : undefined,
      conversion: filters.value.conversion || undefined,
      include_spam: includeSpam.value
    }
    
    console.log('[DEBUG] Fetching metrics with filters:', requestData)

    const getSearchMetrics = httpsCallable(functions, 'getSearchMetrics')
    const result = await getSearchMetrics(requestData)
    
    const data = result.data as { success: boolean; metrics: any }
    
    console.log('[DEBUG] Metrics received:', data.metrics)
    
    if (data.success && data.metrics) {
      metrics.value = {
        searchVolume: data.metrics.searchVolume || 0,
        averageOrderValue: data.metrics.averageOrderValue || 0,
        conversionRate: data.metrics.conversionRate || 0
      }
      metricsInitialized.value = true
    }
  } catch (error) {
    console.error('Error fetching metrics:', error)
    metricsInitialized.value = true // Mark as initialized even on error to prevent infinite loading
  } finally {
    loadingMetrics.value = false
  }
}

// Metric computations
const searchVolume = computed(() => metrics.value.searchVolume)

const formattedAOV = computed(() => {
  if (metrics.value.averageOrderValue === 0) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(metrics.value.averageOrderValue)
})

const formattedConversionRate = computed(() => {
  return `${metrics.value.conversionRate.toFixed(1)}%`
})

// Paginated searches - apply client-side filters to Firestore results
// When text search or category filters are active, paginate filtered results client-side
// Otherwise, show Firestore paginated results with other filters applied
const paginatedSearches = computed(() => {
  const hasTextSearch = searchTerm.value && searchTerm.value.trim().length > 0
  const hasCategoryFilters = filters.value.categories.length > 0
  const needsClientSidePagination = hasTextSearch || hasCategoryFilters
  
  // When using Firestore pagination, conversion filter is already applied in the query
  // Only apply conversion filter client-side when fetching all results (text search or category filters active)
  
  // Apply filters
  let filtered = searches.value.filter(search => {
    // Text search filter
    const matchesSearch = !searchTerm.value || 
      search.query?.toLowerCase().includes(searchTerm.value.toLowerCase())

    // Categories filter
    const matchesCategories = filters.value.categories.length === 0 || 
      filters.value.categories.every(category => 
        search.enriched_data?.enriched_categories?.includes(category)
      )

    // Conversion filter - only apply client-side when using client-side pagination
    // (when using Firestore pagination, conversion is already filtered in the query)
    const matchesConversion = needsClientSidePagination 
      ? (!filters.value.conversion || 
         (filters.value.conversion === 'converted' && search.has_converted) ||
         (filters.value.conversion === 'not_converted' && !search.has_converted))
      : true // Already filtered in Firestore query

    // Spam filter
    const matchesSpam = includeSpam.value || !search.enriched_data?.is_potential_spam

    return matchesSearch && matchesCategories && matchesConversion && matchesSpam
  })
  
  // If text search or category filters are active, paginate client-side
  // Otherwise, return all filtered results (Firestore pagination already applied)
  if (needsClientSidePagination) {
    const start = (currentPage.value - 1) * pageSize.value
    const end = start + pageSize.value
    return filtered.slice(start, end)
  }
  
  // No text search or category filters - return filtered Firestore results
  return filtered
})

// Pagination computed - using Firestore pagination or client-side pagination
const hasTextSearch = computed(() => searchTerm.value && searchTerm.value.trim().length > 0)
const hasCategoryFilters = computed(() => filters.value.categories.length > 0)
const needsClientSidePagination = computed(() => hasTextSearch.value || hasCategoryFilters.value)

const filteredSearchesCount = computed(() => {
  // Only used for client-side pagination, so conversion filter should be applied client-side
  const hasTextSearch = searchTerm.value && searchTerm.value.trim().length > 0
  const hasCategoryFilters = filters.value.categories.length > 0
  const needsClientSidePagination = hasTextSearch || hasCategoryFilters
  
  return searches.value.filter(search => {
    const matchesSearch = !searchTerm.value || 
      search.query?.toLowerCase().includes(searchTerm.value.toLowerCase())
    const matchesCategories = filters.value.categories.length === 0 || 
      filters.value.categories.every(category => 
        search.enriched_data?.enriched_categories?.includes(category)
      )
    // Only apply conversion filter client-side when using client-side pagination
    // (when using Firestore pagination, conversion is already filtered in the query)
    const matchesConversion = needsClientSidePagination
      ? (!filters.value.conversion || 
         (filters.value.conversion === 'converted' && search.has_converted) ||
         (filters.value.conversion === 'not_converted' && !search.has_converted))
      : true // Already filtered in Firestore query
    const matchesSpam = includeSpam.value || !search.enriched_data?.is_potential_spam
    return matchesSearch && matchesCategories && matchesConversion && matchesSpam
  }).length
})

const hasPreviousPage = computed(() => currentPage.value > 1)
const hasMorePages = ref(false) // Track if there are more pages from Firestore
const hasNextPage = computed(() => {
  if (needsClientSidePagination.value) {
    // Client-side pagination: check if there are more filtered results
    return currentPage.value * pageSize.value < filteredSearchesCount.value
  }
  // Firestore pagination: use the hasMorePages ref
  return hasMorePages.value
})

// Pagination methods for Firestore pagination or client-side pagination
const nextPage = async () => {
  if (hasNextPage.value) {
    currentPage.value++
    // Only fetch from Firestore if not using client-side pagination
    if (!needsClientSidePagination.value) {
      await fetchSearches()
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const previousPage = async () => {
  if (hasPreviousPage.value) {
    currentPage.value--
    // Only fetch from Firestore if not using client-side pagination
    if (!needsClientSidePagination.value) {
      await fetchSearches()
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const goToFirstPage = async () => {
  if (currentPage.value !== 1) {
    currentPage.value = 1
    // Only fetch from Firestore if not using client-side pagination
    if (!needsClientSidePagination.value) {
      await fetchSearches(true)
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Methods
const refreshSearches = async () => {
  currentPage.value = 1
  await fetchSearches(true) // Reset pagination
  await fetchMetrics()
}

// Debounce timer for search term
let searchTermDebounceTimer: ReturnType<typeof setTimeout> | null = null

// Watch searchTerm with debounce (500ms delay)
watch(searchTerm, () => {
  console.log('[DEBUG] Search term changed, debouncing fetch')
  // Show loading state immediately when user types
  loading.value = true
  loadingMetrics.value = true
  // Clear existing timer
  if (searchTermDebounceTimer) {
    clearTimeout(searchTermDebounceTimer)
  }
  // Set new timer
  searchTermDebounceTimer = setTimeout(async () => {
    currentPage.value = 1
    // Refetch searches when text search changes (to get all results for client-side filtering)
    await fetchSearches(true) // Reset pagination
    fetchMetrics() // Fetch metrics independently, don't block page
    searchTermDebounceTimer = null
  }, 500) // 500ms debounce
})

// Watch other filters without debounce (immediate update)
watch(
  () => ({
    categories: filters.value.categories,
    conversion: filters.value.conversion,
    includeSpam: includeSpam.value
  }),
  () => {
    console.log('[DEBUG] Filters changed, fetching metrics immediately', {
      categories: filters.value.categories,
      conversion: filters.value.conversion,
      includeSpam: includeSpam.value
    })
    currentPage.value = 1
    // Refetch searches to reset pagination when filters change
    fetchSearches(true) // Reset pagination
    fetchMetrics()
  },
  { deep: true }
)

const formatKey = (key: string | number): string => {
  const keyStr = String(key)
  return keyStr
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const formatCategory = (category: string): string => {
  if (!category) return ''
  // Replace hyphens and underscores with spaces, then capitalize first letter of each word
  return category
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const formatDate = (date: any) => {
  if (!date) return ''
  // If it's a Firestore Timestamp, convert to date
  const dateObj = date instanceof Timestamp ? date.toDate() : new Date(date)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(dateObj)
}

// Watch for filter changes - reset pagination when filters change
watch([() => selectedStartDate.value, () => selectedEndDate.value], () => {
  if (selectedStartDate.value && selectedEndDate.value) {
    currentPage.value = 1
    fetchSearches(true) // Reset pagination
    fetchMetrics()
  }
})

// Handle route query parameters
import { useRoute } from 'vue-router'
const route = useRoute()

// Initialize
onMounted(async () => {
  await orgStore.fetchOrgData()
  
  // Check for category in route query and apply filter
  const categoryParam = route.query.category as string
  if (categoryParam) {
    filters.value.categories = [categoryParam]
  }
  
  // Fetch searches and metrics independently
  fetchSearches(true) // Reset pagination on initial load
  fetchMetrics() // Fetch metrics independently, don't block page
})

// Watch for route changes to update filters
watch(
  () => route.query.category,
  (newCategory) => {
    if (newCategory && typeof newCategory === 'string') {
      filters.value.categories = [newCategory]
    } else {
      filters.value.categories = []
    }
  }
)
</script>